import React, { useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import authService from '../../services/authService';
import FormField from '../common/FormField';
import LoadingSpinner from '../common/LoadingSpinner';
import Alert from '../common/Alert';
import './MfaSetup.css';

const MfaSetup = ({ onComplete }) => {
  const [setupData, setSetupData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [code, setCode] = useState('');
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    const fetchSetup = async () => {
      try {
        const data = await authService.setupMfa();
        setSetupData(data);
      } catch (err) {
        setError('Failed to initialize MFA setup. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    fetchSetup();
  }, []);

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!code || code.length !== 6) {
      setError('Please enter a valid 6-digit code');
      return;
    }
    
    setVerifying(true);
    setError('');
    try {
      await authService.verifyMfa(code, setupData?.secret);
      if (onComplete) onComplete();
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid verification code');
    } finally {
      setVerifying(false);
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="mfa-setup">
      <h3 className="mfa-setup__title">Set up Two-Factor Authentication</h3>
      
      {error && <Alert type="error" message={error} />}
      
      <div className="mfa-setup__steps">
        <div className="mfa-step">
          <span className="mfa-step__num">1</span>
          <p>Install an authenticator app like Google Authenticator or Authy on your mobile device.</p>
        </div>
        
        <div className="mfa-step">
          <span className="mfa-step__num">2</span>
          <p>Scan this QR code with the app, or enter the secret key manually.</p>
        </div>
        
        {setupData && (
          <div className="mfa-setup__qr-container">
            <div className="qr-placeholder">
              <QRCodeSVG value={setupData.qr_code_url} size={150} title="MFA QR Code" />
            </div>
            <p className="mfa-setup__secret">Secret Key: <strong>{setupData.secret}</strong></p>
          </div>
        )}

        <div className="mfa-step">
          <span className="mfa-step__num">3</span>
          <p>Enter the 6-digit code generated by your app to verify setup.</p>
        </div>
      </div>

      <form onSubmit={handleVerify} className="mfa-setup__form">
        <FormField
          label="Verification Code"
          name="code"
          type="text"
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          placeholder="000000"
          required
        />
        <button type="submit" className="mfa-setup__submit" disabled={verifying || code.length !== 6}>
          {verifying ? 'Verifying...' : 'Verify and Enable'}
        </button>
      </form>
    </div>
  );
};

export default MfaSetup;
